// The Recipes tab's pure half: tags, filters, search, what a recipe (or several,
// or a week of planned meals) needs from the shop, and the generated cover.
// Ported from the approved prototype (feature pack reference/src/recipes.ts) onto
// the repo's types: amounts are {amount, unit} (recipe.ts), and "the same
// ingredient" is `nameKey`, not a lowercase compare, so "Lime" from one recipe
// and "lime" from another — or "lök" and "onion" — land on one line.

import type { Category, Item, Member, Recipe, RecipeIngredient, RecipeSource } from './supabase'
import { fold, itemKey } from './normalize'
import { displayAmount, mergeAmounts, roundAmount, scaledAmount, type Amount } from './recipe'
import { recipeStatus, scaleFactorFor } from './recipeMatch'
import { isVegetarian } from './vegetarian'

// ── tags ──────────────────────────────────────────────────────────────────────

export const TAG_MAX = 24

/** Trimmed, spaces collapsed, first letter capitalised, at most 24 characters. */
export function normalizeTag(raw: string): string {
  const t = (raw || '').trim().replace(/\s+/g, ' ').slice(0, TAG_MAX).trim()
  return t ? t[0].toUpperCase() + t.slice(1) : ''
}

/** The tags after adding one, reusing the spelling of a household tag that
 *  differs only in case; a tag the recipe already has is ignored. */
export function addTag(tags: string[], raw: string, existing: string[]): string[] {
  let name = normalizeTag(raw)
  if (!name) return tags
  const known = existing.find(t => t.toLowerCase() === name.toLowerCase())
  if (known) name = known
  if (tags.some(t => t.toLowerCase() === name.toLowerCase())) return tags
  return [...tags, name]
}

export const removeTag = (tags: string[], name: string): string[] => tags.filter(t => t !== name)

export interface TagCount { name: string; count: number }

/** Every tag in the household, most used first, then by name. */
export function allTags(recipes: Pick<Recipe, 'tags'>[]): TagCount[] {
  const counts = new Map<string, number>()
  for (const r of recipes) for (const t of r.tags) counts.set(t, (counts.get(t) ?? 0) + 1)
  return [...counts]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
}

/** Up to `max` household tags the recipe doesn't have yet, most used first. */
export function tagSuggestions(tags: string[], household: TagCount[], max = 3): string[] {
  const have = new Set(tags.map(t => t.toLowerCase()))
  return household.filter(t => !have.has(t.name.toLowerCase())).slice(0, max).map(t => t.name)
}

// ── filters and search ────────────────────────────────────────────────────────

export type RecipeFilter = 'all' | 'starred' | 'quick' | 'veg' | 'rated'
export const QUICK_MINUTES = 30
/** "4+ stars": the page's own rating (039), so only imported pages have one. */
export const GOOD_RATING = 4

export function passesFilter(r: Recipe, f: RecipeFilter): boolean {
  if (f === 'starred') return r.starred
  if (f === 'quick') return r.minutes != null && r.minutes <= QUICK_MINUTES
  if (f === 'veg') return isVegetarian(r)
  if (f === 'rated') return r.rating_value != null && r.rating_value >= GOOD_RATING
  return true
}

/** Name, any ingredient or any tag contains the query — case and accents ignored. */
export function matchesSearch(r: Recipe, q: string): boolean {
  const s = fold(q)
  if (!s) return true
  return fold(r.title).includes(s)
    || r.ingredients.some(g => fold(g.name).includes(s))
    || r.tags.some(t => fold(t).includes(s))
}

export function visibleRecipes(recipes: Recipe[], f: RecipeFilter, tag: string | null, q: string, source: RecipeSource | null = null): Recipe[] {
  return recipes.filter(r => passesFilter(r, f) && (!tag || r.tags.includes(tag)) && (!source || r.source === source) && matchesSearch(r, q))
}

export const SOURCE_LABELS: Record<RecipeSource, string> = {
  web: 'Websites', youtube: 'YouTube', tiktok: 'TikTok', instagram: 'Instagram',
}

/** The sources the household's recipes came from, with counts, in a fixed order — the Source filter's chips. */
export function recipeSources(recipes: Pick<Recipe, 'source'>[]): { source: RecipeSource; count: number }[] {
  const order: RecipeSource[] = ['web', 'youtube', 'tiktok', 'instagram']
  return order
    .map(source => ({ source, count: recipes.filter(r => r.source === source).length }))
    .filter(s => s.count > 0)
}

// ── what to shop for ──────────────────────────────────────────────────────────

/**
 * One line of a shopping set: an ingredient of one or more recipes, amounts
 * scaled to the chosen servings and summed per unit ("1 pack + 400 g" stays two
 * parts — we never convert between units).
 */
export interface Need {
  key: string
  /** What goes on the list: the household's own name for it when it has one. */
  name: string
  /** The recipe's own spelling, so the choices sheet can say what was matched and undo it. */
  original: string
  parts: Amount[]
  /** An amount typed over the recipe's in the choices sheet; it replaces `parts`. */
  quantityText?: string
  /** Every recipe it came from marked it a pantry staple. */
  pantry: boolean
  from: string[]
  /** The category the first recipe that has one picked for it. */
  categoryId?: string
}

/**
 * Where an ingredient goes: the category a member picked for it while that
 * category still exists, else the household's guess (App's categoryFor:
 * category memory, then keywords).
 */
export function ingredientCategory(
  g: Pick<RecipeIngredient, 'name' | 'category_id'>,
  categoryFor: (name: string) => string | null,
  categories: Pick<Category, 'id'>[],
): string | null {
  if (g.category_id && categories.some(c => c.id === g.category_id)) return g.category_id
  return categoryFor(g.name)
}

function addPart(parts: Amount[], add: Amount): Amount[] {
  const i = parts.findIndex(p => mergeAmounts(p, add) !== null)
  if (i < 0) return [...parts, add]
  const sum = mergeAmounts(parts[i], add)!
  return parts.map((p, k) => (k === i ? roundAmount(sum.amount, sum.unit) : p))
}

/** The amounts of a need as one line: "600 g", "1 pack + 400 g", "" for none. */
export function needQuantity(need: Pick<Need, 'parts' | 'quantityText'>, locale?: string): string {
  if (need.quantityText !== undefined) return need.quantityText.trim()
  return need.parts.map(p => displayAmount(p.amount, p.unit, locale)).join(' + ')
}

/** One recipe at `servings` (its own when null), shared ingredients folded once. */
export function recipeNeeds(
  recipe: Pick<Recipe, 'title' | 'servings' | 'ingredients'>,
  servings: number | null,
  spellingFor?: (name: string) => string,
): Need[] {
  return combineNeeds([{ recipe, servings }], spellingFor)
}

/**
 * Several recipes or planned meals into one shopping set: an ingredient two of
 * them share appears once, with the amounts added up where the units agree.
 * Order is first appearance.
 *
 * `spellingFor` names each need the way the household already does (lib/catalog.ts
 * `spellingFor`): a recipe's "fast potatis" goes on the list as the "potatis fast"
 * bought last week, rather than as a second name for the same thing.
 */
export function combineNeeds(
  entries: { recipe: Pick<Recipe, 'title' | 'servings' | 'ingredients'>; servings: number | null }[],
  spellingFor: (name: string) => string = name => name,
): Need[] {
  const byKey = new Map<string, Need>()
  for (const { recipe, servings } of entries) {
    const factor = scaleFactorFor(recipe, servings)
    for (const g of recipe.ingredients) {
      const name = g.name.trim()
      if (!name) continue
      const key = itemKey(name) || fold(name)
      const amount = scaledAmount(g.amount, g.unit, factor)
      const cur = byKey.get(key)
      if (!cur) {
        byKey.set(key, {
          key, name: spellingFor(name), original: name, parts: amount ? [amount] : [], pantry: g.pantry === true, from: [recipe.title],
          ...(g.category_id ? { categoryId: g.category_id } : {}),
        })
        continue
      }
      if (!cur.categoryId && g.category_id) cur.categoryId = g.category_id
      if (amount) cur.parts = addPart(cur.parts, amount)
      cur.pantry = cur.pantry && g.pantry === true
      if (!cur.from.includes(recipe.title)) cur.from.push(recipe.title)
    }
  }
  return [...byKey.values()]
}

/** Shopping-relevant ingredient count (pantry staples left out), as on the cards. */
export const ingredientCount = (r: Pick<Recipe, 'ingredients'>) => r.ingredients.filter(g => !g.pantry).length

/** Ingredients that are neither on the list nor in the basket. */
export const missingCount = (r: Pick<Recipe, 'servings' | 'ingredients'>, items: Item[]) =>
  recipeStatus(r, items).missing.length

// ── card details ──────────────────────────────────────────────────────────────

/** "koket.se" for an imported page, "TikTok" for a video, "Added by Anna" otherwise. */
export function recipeSource(r: Pick<Recipe, 'url' | 'created_by' | 'source'>, members: Pick<Member, 'id' | 'display_name'>[]): string {
  if (r.source && r.source !== 'web') return SOURCE_LABELS[r.source]
  if (r.url) {
    try { return new URL(r.url).hostname.replace(/^www\./, '') } catch { /* fall through */ }
  }
  const by = members.find(m => m.id === r.created_by)?.display_name
  return by ? `Added by ${by}` : ''
}

export interface Cover {
  /** The category with the most ingredients — the cover's tint. Null without any. */
  categoryId: string | null
  initial: string
  /** One segment per ingredient, in category order; null = no category. */
  segments: (string | null)[]
}

/**
 * The generated cover: no photos, so a recipe is drawn from what it's made of —
 * the tint of its biggest category, its initial, and one segment per ingredient.
 * `categoryIdOf` is the household's own guess for a name (resolveCategoryId), so
 * a recipe's colours follow the categories its items land in on the list.
 */
export function coverModel(
  r: Pick<Recipe, 'title' | 'ingredients'>,
  categoryIdOf: (name: string) => string | null,
  categories: Pick<Category, 'id'>[],
): Cover {
  const ids = r.ingredients.filter(g => !g.pantry).map(g => ingredientCategory(g, categoryIdOf, categories))
  const counts = new Map<string, number>()
  for (const id of ids) if (id) counts.set(id, (counts.get(id) ?? 0) + 1)
  let top: string | null = null
  for (const c of categories) if ((counts.get(c.id) ?? 0) > (top ? counts.get(top)! : 0)) top = c.id
  const known = new Set(categories.map(c => c.id))
  const segments = [
    ...categories.flatMap(c => ids.filter(id => id === c.id)),
    ...ids.filter(id => !id || !known.has(id)).map(() => null),
  ]
  return { categoryId: top, initial: (r.title.trim()[0] ?? '').toUpperCase(), segments }
}
